/**
 * @file The single table of everything one restore would change (SYD-2450): the form
 * version's fields and references, then each ArTable's rows, in one list under one
 * set of columns. Consumer: `FormRecoveryConfirmModalContent`, which resolves every
 * name and hands the groups down already built.
 *
 * One table rather than one per source, because to the operator the ArTable is part
 * of the form and this is one restore. A group is a heading row spanning the columns;
 * a group with no heading is a form whose moment named no table, which renders exactly
 * the three-column list this dialog has always shown.
 *
 * A row entry states what happens to the row — the cells that change, a row coming
 * back, a row going to the trash — and its per-cell detail expands into the SAME
 * `Value now` / `Value being restored` columns rather than a nested table of its own.
 * The expansion is dataSource rows, not antd's `expandable`, so the detail is under
 * the columns that name it.
 *
 * Presentational only: it reads no store and holds nothing but which rows are open.
 */

import { FC, useState } from 'react';
import { Button, Table, Tag, Typography, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import type { ArTableRowDiffEntry, FormPitrValue } from '#types';
import FlexBox from '../../atoms/FlexBox';
import { formatPitrValue } from './formatPitrValue';

const { Text } = Typography;

/** One restorable cell or row key of a row entry, already named for the screen. */
export type RecoveryChangeCell = {
  key: string;
  /** The column's own name, or the row key's word in the domain — never a coined one. */
  name: string;
  live: FormPitrValue | null;
  retained: FormPitrValue | null;
};

/**
 * One line of the confirmation. `value` is a field, a reference or an expanded cell —
 * anything with a retained side and a live one. `row` is a whole ArTable row, which has
 * a label and an action instead. `notice` is a statement about its group: a table still
 * being read, a table that could not be read, or the entries left off the screen.
 */
export type RecoveryChangeEntry =
  | {
      kind: 'value';
      key: string;
      name: string;
      live: FormPitrValue | null;
      retained: FormPitrValue | null;
    }
  | {
      kind: 'row';
      key: string;
      /** The row's first-column value, formatted, or its id when that column is empty. */
      name: string;
      action: ArTableRowDiffEntry['action'];
      /** The live row is locked, so this restore leaves it exactly as it is. */
      locked: boolean;
      /** A `_trashedRows` entry still holds it, so coming back is a recovery from there. */
      inTrash: boolean;
      cells: readonly RecoveryChangeCell[];
    }
  | { kind: 'notice'; key: string; text: string };

export type RecoveryChangeGroup = {
  key: string;
  /**
   * The source's own name — the form version as the rail names it, or the table's
   * `shortLabel`. `null` draws no heading at all, which is the single-source shape.
   */
  name: string | null;
  entries: readonly RecoveryChangeEntry[];
};

/** A row of the rendered table, which is a group's entries plus its heading and detail. */
type ChangeRow =
  | { kind: 'group'; key: string; name: string }
  | {
      kind: 'value';
      key: string;
      name: string;
      /** An expanded cell of a row entry, indented under it. */
      detail: boolean;
      live: FormPitrValue | null;
      retained: FormPitrValue | null;
    }
  | { kind: 'row'; key: string; entry: Extract<RecoveryChangeEntry, { kind: 'row' }> }
  | { kind: 'notice'; key: string; text: string };

/** What this restore does to one row, in the words the summary above uses. */
const rowChangePhrase = (entry: Extract<RecoveryChangeEntry, { kind: 'row' }>): string => {
  const count = entry.cells.length;
  const cells = `${count} ${count === 1 ? 'cell' : 'cells'}`;
  if (entry.action === 'delete') return 'This row goes to the trash.';
  if (entry.action === 'recreate') {
    return entry.inTrash
      ? `This row comes back from the trash, with ${cells}.`
      : `This row comes back, with ${cells}.`;
  }
  return `${cells} ${count === 1 ? 'changes' : 'change'} in this row.`;
};

const columnsOf = (
  toggle: (key: string) => void,
  openKeys: readonly string[],
  detailIndent: number,
): ColumnsType<ChangeRow> => [
  {
    title: 'Field or row',
    key: 'name',
    onCell: (row) => ({ colSpan: row.kind === 'group' || row.kind === 'notice' ? 3 : 1 }),
    render: (_, row) => {
      if (row.kind === 'group') return <Text strong>{row.name}</Text>;
      if (row.kind === 'notice') return <Text type='secondary'>{row.text}</Text>;
      if (row.kind === 'value') {
        return (
          <Text style={row.detail ? { paddingInlineStart: detailIndent } : undefined}>
            {row.name}
          </Text>
        );
      }
      return (
        <FlexBox justifyStart gap={5} noGrow stretch wrap>
          <Text>{row.entry.name}</Text>
          {row.entry.locked && (
            <Tag color='warning' variant='filled' style={{ margin: 0 }}>
              Locked
            </Tag>
          )}
        </FlexBox>
      );
    },
  },
  {
    title: 'Value now',
    key: 'live',
    onCell: (row) => ({
      colSpan: row.kind === 'group' || row.kind === 'notice' ? 0 : row.kind === 'row' ? 2 : 1,
    }),
    render: (_, row) => {
      if (row.kind === 'value') return formatPitrValue(row.live);
      if (row.kind !== 'row') return null;
      const open = openKeys.includes(row.key);
      return (
        <FlexBox justifyStart gap={5} noGrow stretch wrap>
          <Text type={row.entry.locked ? 'secondary' : undefined}>
            {row.entry.locked
              ? `${rowChangePhrase(row.entry)} It is locked, so this restore leaves it as it is.`
              : rowChangePhrase(row.entry)}
          </Text>
          {row.entry.cells.length > 0 && (
            <Button
              type='text'
              size='small'
              onClick={() => toggle(row.key)}
              aria-label={`${open ? 'Hide' : 'Show'} the cells of ${row.entry.name}`}
              aria-expanded={open}
              style={{ paddingInline: 0 }}
            >
              {open ? 'Hide the cells' : 'Show the cells'}
            </Button>
          )}
        </FlexBox>
      );
    },
  },
  {
    title: 'Value being restored',
    key: 'retained',
    onCell: (row) => ({ colSpan: row.kind === 'value' ? 1 : 0 }),
    render: (_, row) => (row.kind === 'value' ? formatPitrValue(row.retained) : null),
  },
];

const rowsOf = (
  groups: readonly RecoveryChangeGroup[],
  openKeys: readonly string[],
): ChangeRow[] =>
  groups.flatMap<ChangeRow>((group) => [
    ...(group.name === null
      ? []
      : [{ kind: 'group' as const, key: `group:${group.key}`, name: group.name }]),
    ...group.entries.flatMap<ChangeRow>((entry) => {
      if (entry.kind === 'notice') return [{ kind: 'notice', key: entry.key, text: entry.text }];
      if (entry.kind === 'value') {
        return [
          {
            kind: 'value',
            key: entry.key,
            name: entry.name,
            detail: false,
            live: entry.live,
            retained: entry.retained,
          },
        ];
      }
      return [
        { kind: 'row', key: entry.key, entry },
        ...(openKeys.includes(entry.key)
          ? entry.cells.map<ChangeRow>((cell) => ({
              kind: 'value',
              key: `${entry.key}:${cell.key}`,
              name: cell.name,
              detail: true,
              live: cell.live,
              retained: cell.retained,
            }))
          : []),
      ];
    }),
  ]);

export type FormRecoveryChangeTableProps = {
  groups: readonly RecoveryChangeGroup[];
};

const FormRecoveryChangeTable: FC<FormRecoveryChangeTableProps> = ({ groups }) => {
  const { token } = theme.useToken();
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const toggle = (key: string): void =>
    setOpenKeys(
      openKeys.includes(key) ? openKeys.filter((open) => open !== key) : [...openKeys, key],
    );

  return (
    <Table<ChangeRow>
      size='small'
      pagination={false}
      aria-label='Values this restore will change'
      dataSource={rowsOf(groups, openKeys)}
      columns={columnsOf(toggle, openKeys, token.paddingLG)}
      rowKey={(row) => row.key}
    />
  );
};

FormRecoveryChangeTable.displayName = 'FormRecoveryChangeTable';

export default FormRecoveryChangeTable;
